import type {GraphQLQuery} from '@aws-amplify/api';
import {generateClient} from 'aws-amplify/api';

import {getProduct, listProducts} from './queries';
import {createProduct, updateProduct} from './mutations';
import type {
    CreateProduct,
    CreateProductInput,
    GetProduct,
    ListProducts,
    UpdateProduct,
    UpdateProductInput,
} from '../types';

// This layer only calls the API. A failure propagates as the rejection the
// client raised; the notification belongs to the React Query hook that called
// us (hooks/useProductsQuery.ts), so a failed call is both shown and recorded
// as a failure by the query cache.
const client = generateClient();

export const fetchRecords = async (limit: number, nextToken?: string | null) => {
    const response = await client.graphql<GraphQLQuery<ListProducts>>({
        query: listProducts,
        variables: {
            limit,
            ...(nextToken ? {nextToken} : {}),
        },
    });

    return {
        items: response.data?.listProducts?.items ?? [],
        nextToken: response.data?.listProducts?.nextToken ?? null,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetProduct>>({
        query: getProduct,
        variables: {id},
    });
    return response.data?.getProduct;
};

// The caller passes the WHITELISTED input built by forms/buildInput.ts. The
// service sends it as it is and never reshapes a record.
export const create = async (input: CreateProductInput) => {
    const response = await client.graphql<GraphQLQuery<CreateProduct>>({
        query: createProduct,
        variables: {input},
    });
    return response.data?.createProduct;
};

export const update = async (input: UpdateProductInput) => {
    const response = await client.graphql<GraphQLQuery<UpdateProduct>>({
        query: updateProduct,
        variables: {input},
    });
    return response.data?.updateProduct;
};
